import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Avatar,
  IconButton,
  useTheme,
  alpha,
  styled,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip
} from '@mui/material';
import {
  LocalShipping as ShipmentIcon,
  Person as PersonIcon,
  Update as UpdateIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  '&:last-child': {
    marginBottom: 0,
  },
}));

const TimeChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.text.secondary, 0.08),
  color: theme.palette.text.secondary,
  fontSize: '0.7rem',
  height: 20,
  '& .MuiChip-label': {
    padding: '0 6px',
  },
}));

interface Activity {
  id: number;
  type: string;
  message: string;
  timestamp: string;
  user: string;
}

interface RecentActivitiesProps {
  activities: Activity[];
  title?: string;
  maxItems?: number;
}

export default function RecentActivities({ 
  activities, 
  title = "Recent Activities",
  maxItems = 8 
}: RecentActivitiesProps) {
  const theme = useTheme();

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'shipment_created':
      case 'shipment_updated':
        return <ShipmentIcon />;
      case 'driver_status_changed':
        return <PersonIcon />;
      case 'shipment_delivered':
        return <CheckIcon />;
      case 'shipment_cancelled':
        return <CancelIcon />;
      default:
        return <UpdateIcon />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'shipment_created':
        return theme.palette.primary.main;
      case 'shipment_delivered':
        return theme.palette.success.main;
      case 'shipment_cancelled':
        return theme.palette.error.main;
      case 'driver_status_changed':
        return theme.palette.info.main;
      default:
        return theme.palette.secondary.main;
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    return date.toLocaleDateString();
  };

  const displayedActivities = activities.slice(0, maxItems);

  return (
    <StyledCard>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Latest system activities
          </Typography>
        }
        action={
          <IconButton size="small">
            <MoreVertIcon />
          </IconButton>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0, pb: 2 }}>
        <List sx={{ py: 0 }}>
          {displayedActivities.map((activity) => (
            <StyledListItem key={activity.id} alignItems="flex-start">
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor: alpha(getActivityColor(activity.type), 0.1),
                    color: getActivityColor(activity.type),
                    width: 40,
                    height: 40,
                  }}
                >
                  {getActivityIcon(activity.type)}
                </Avatar>
              </ListItemAvatar>
              
              <ListItemText
                primaryTypographyProps={{ component: 'div' }}
                primary={
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        color: 'text.primary',
                        flex: 1,
                        mr: 1
                      }}
                    >
                      {activity.message}
                    </Typography>
                    <TimeChip 
                      label={formatTimeAgo(activity.timestamp)}
                      size="small"
                    />
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    by {activity.user}
                  </Typography>
                }
              />
            </StyledListItem>
          ))}
        </List>

        {activities.length === 0 && (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center"
            py={4}
          >
            <UpdateIcon 
              sx={{ 
                fontSize: 48, 
                color: 'text.secondary', 
                opacity: 0.5,
                mb: 2 
              }} 
            />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No recent activities
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
}
